import React from "react";
import { getImageUrl } from "helpers/Image.helper";
import { formatTimeAgo } from "helpers/Datetime.helper";
import { getCurrentLangName } from "helpers/String.helper";
import { IDiaryComment } from "api/interfaces/profile.interface";

interface CommentItemProps {
    comment: IDiaryComment;
    isChild?: boolean;
    onLikeToggle: (commentId: number) => void;
    onReplyClick: (commentId: number) => void;
    onDeleteClick: (commentId: number) => void;
    onReportClick: (commentId: number, content: string) => void;
    isMyComment: (comment: IDiaryComment) => boolean;
    commentLikeLoading: { [key: number]: boolean };
    showKebabMenu: number | null;
    onKebabToggle: (commentId: number) => void;
    kebabRef: React.RefObject<HTMLDivElement>;
    onUserClick?: (userId: string, userType: string) => void;
    // 번역 관련 props
    isTranslating?: boolean;
    translatedContent?: string;
    onTranslate?: (commentId: number) => void;
}

const CommentItem: React.FC<CommentItemProps> = ({
    comment,
    isChild = false,
    onLikeToggle,
    onReplyClick,
    onDeleteClick,
    onReportClick,
    isMyComment,
    commentLikeLoading,
    showKebabMenu,
    onKebabToggle,
    kebabRef,
    onUserClick,
    isTranslating = false,
    translatedContent,
    onTranslate
}) => {
    const isLiking = commentLikeLoading[comment.id];
    const isActiveKebab = showKebabMenu && comment.id === showKebabMenu;

    return (
        <div className={`${isChild ? '' : ''} ${isChild ? '' : 'py-1 '}`}>
            {/* 코멘트 헤더 */}
            <div className="flex items-center space-x-2 mb-1.5">
                {/* 프로필 이미지/로고 */}
                <div className={`${isChild ? 'w-6 h-6' : 'w-7 h-7'} rounded-full overflow-hidden flex-shrink-0`}>
                    {comment.user?.userStudent && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onUserClick && comment.user?.userStudent?.id) {
                                    onUserClick(comment.user.userStudent.id.toString(), 'student');
                                }
                            }}
                            className="w-full h-full hover:opacity-80 transition-opacity cursor-pointer"
                        >
                            <img
                                src={getImageUrl(comment.user?.userStudent?.profileImage) || "/imgs/dummy/basic-img.png"}
                                alt="프로필"
                                className="w-full h-full object-cover"
                            />
                        </button>
                    )}
                    {comment.user?.userKoach && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onUserClick && comment.user?.userKoach?.id) {
                                    onUserClick(comment.user.userKoach.id.toString(), 'koach');
                                }
                            }}
                            className="w-full h-full hover:opacity-80 transition-opacity cursor-pointer"
                        >
                            <img
                                src={getImageUrl(comment.user?.userKoach?.profileImage) || "/imgs/dummy/basic-img.png"}
                                alt="프로필"
                                className="w-full h-full object-cover"
                            />
                        </button>
                    )}
                    {comment.user?.userSchool && (
                        <img
                            src={getImageUrl(comment.user?.userSchool?.logoImageId) || "/imgs/dummy/school-logo/school-logo-1.png"}
                            alt="학원 로고"
                            className="w-full h-full object-cover"
                        />
                    )}
                    {comment.user?.userAgency && (
                        <img
                            src={getImageUrl(comment.user?.userAgency?.agency.logoImage) || "/imgs/dummy/agency-logo/agency-logo-1.png"}
                            alt="기획사 로고"
                            className="w-full h-full object-cover"
                        />
                    )}
                </div>

                {/* 사용자 정보 */}
                <div className="flex flex-col flex-1">
                    <span className={`font-medium text-gray-900 ${isChild ? 'text-xs' : 'text-sm'}`}>
                        {comment.user?.userStudent && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (onUserClick && comment.user?.userStudent?.id) {
                                        onUserClick(comment.user.userStudent.id.toString(), 'student');
                                    }
                                }}
                                className="hover:text-[#CF3688] hover:underline cursor-pointer transition-colors"
                            >
                                {comment.user.userStudent.nickname ||
                                    `${comment.user.userStudent.firstName} ${comment.user.userStudent.lastName}` ||
                                    "알 수 없음"}
                            </button>
                        )}
                        {comment.user?.userKoach && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (onUserClick && comment.user?.userKoach?.id) {
                                        onUserClick(comment.user.userKoach.id.toString(), 'koach');
                                    }
                                }}
                                className="hover:text-[#CF3688] hover:underline cursor-pointer transition-colors"
                            >
                                {comment.user.userKoach.nickname ||
                                    `${comment.user.userKoach.firstName} ${comment.user.userKoach.lastName}` ||
                                    "알 수 없음"}
                            </button>
                        )}
                        {comment.user?.userSchool && (
                            comment.user.userSchool.schoolName || "알 수 없음"
                        )}
                        {comment.user?.userAgency && (
                            comment.user.userAgency.agency.name || "알 수 없음"
                        )}
                    </span>
                    <span className="text-xs text-gray-500">
                        {getCurrentLangName(comment.user?.accountType?.names) || comment.user?.accountTypeCode}
                    </span>
                </div>

                {/* 케밥 버튼 - 모든 댓글에 표시 */}
                <div className="relative flex-shrink-0 flex items-center gap-1">
                    {/* 번역 버튼 */}
                    {onTranslate && (
                        <button
                            onClick={() => onTranslate(comment.id)}
                            disabled={isTranslating}
                            className={`p-1 rounded-full transition-all duration-200 ${
                                isTranslating
                                    ? 'opacity-50 cursor-not-allowed'
                                    : translatedContent
                                    ? 'hover:bg-[#CF3688]/10 text-[#CF3688]'
                                    : 'hover:bg-gray-100 text-gray-500'
                            }`}
                        >
                            {isTranslating ? (
                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                <img 
                                    src={translatedContent ? "/imgs/icons/icon-translate-active.svg" : "/imgs/icons/icon-translate.svg"} 
                                    alt="translate" 
                                    className="w-4 h-4" 
                                />
                            )}
                        </button>
                    )}
                    
                    {/* 케밥 메뉴 버튼 */}
                    <button
                        onClick={() => onKebabToggle(comment.id)}
                        className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                        </svg>
                    </button>

                    {/* 케밥 메뉴 툴팁 */}
                    {isActiveKebab && (
                        <div
                            ref={kebabRef}
                            className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[120px]"
                        >
                            {isMyComment(comment) ? (
                                <button
                                    onClick={() => onDeleteClick(comment.id)}
                                    className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
                                >
                                    삭제
                                </button>
                            ) : (
                                <button
                                    onClick={() => onReportClick(comment.id, comment.contents)}
                                    className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
                                >
                                    신고
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* 코멘트 내용 */}
            <div className="mb-2">
                <p className={`text-gray-700 break-words overflow-wrap-anywhere ${isChild ? 'text-sm' : ''}`}>{comment.contents}</p>
                {translatedContent && (
                    <p className={`text-gray-500 mt-1 pl-3 border-l-2 border-gray-400 break-words overflow-wrap-anywhere ${isChild ? 'text-sm' : ''}`}>
                        {translatedContent}
                    </p>
                )}
            </div>

            {/* 코멘트 좋아요, 삭제 버튼 및 작성시간 */}
            <div className="flex items-center justify-between">
                {/* 좌측: 좋아요 버튼과 대댓글 버튼 */}
                <div className="flex items-center gap-3">
                    {/* 좋아요 버튼 */}
                    <button
                        className={`flex items-center space-x-1 transition-opacity ${isLiking
                            ? 'opacity-50 cursor-not-allowed'
                            : 'hover:opacity-80 cursor-pointer'
                            }`}
                        onClick={() => onLikeToggle(comment.id)}
                        disabled={isLiking}
                    >
                        {isLiking ? (
                            // 로딩 중일 때 스피너 표시
                            <svg className="w-4 h-4 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            // 좋아요 아이콘
                            <img src={`/imgs/icons/icon-heart${comment.isLiked ? '-active' : ''}.svg`} alt="like" className="w-4 h-4" />
                        )}
                        <span className={`text-xs ${comment.isLiked ? 'text-[#CF3688]' : 'text-gray-500'}`}>
                            {comment.likeCount || 0}
                        </span>
                    </button>

                    {/* 대댓글 버튼 - 메인 댓글에만 표시 */}
                    {!isChild && (
                        <button
                            className="text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
                            onClick={() => onReplyClick(comment.id)}
                        >
                            <img src="/imgs/icons/icon-reply.svg" alt="대댓글" className="w-4 h-4" />
                        </button>
                    )}
                </div>

                {/* 우측: 작성시간 */}
                <div className="flex items-center space-x-3">
                    {/* 작성시간 */}
                    <span className="text-xs text-gray-400">
                        {formatTimeAgo(comment.createdAt)}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default CommentItem;
